// ENG-6035: restart trigger for Slack behaviour-field changes.
//
// The webapp's Slack panel saves allowed_users / thread_auto_follow /
// channel_response_mode via the agent-edit channel_upserts flow. The
// manager's channel-cred hash correctly invalidates on the change and the
// adapter rewrites the slack-channel entry's env in .mcp.json — but the
// running slack-channel MCP child read SLACK_ALLOWED_USERS (and friends)
// from process.env exactly once at boot, so the on-disk rewrite never
// reaches it. Same last-hop gap ENG-5858 closed for sender_policy and
// ENG-6024 closed for Teams behaviour fields: the channel-set restart only
// fires on membership change, and the .mcp.json drift watcher deliberately
// suppresses value-only restarts (ENG-5537, Composio URL-rotation flapping).
//
// This trigger hashes ONLY the operator-editable behaviour subset below, so
// credential/token churn in the wider channel config can't fire it — and the
// subset itself only changes on an explicit operator save, so it can't flap.
//
// Closing this for Slack also retires the documented Slack half of the
// ENG-5537 behaviour-restart gap (thread_auto_follow / channel_response_mode
// previously required a manual session restart to take effect).
//
// ENG-6065: Block Kit flags (`block_kit_enabled` / `block_kit_ask_user_enabled`)
// were INCLUDED here so an operator's mid-session toggle took effect.
// ENG-6504 RETIRED them: Block Kit + ask_user are now permanently on for every
// Slack channel (the adapter hardwires the env to 'true'), so there is no
// operator toggle and their env output is constant — nothing for this trigger
// to track.
//
// ENG-10028: `peer_agent_mode`, `peer_group_ids` and `third_party_bots` are now
// INCLUDED — the FOURTH instance of the last-hop gap named in the header above,
// found live: two channel-config changes 12 minutes apart both reached
// .mcp.json within ~20s and neither ever reached the running child; the second
// sat on disk and not in the process for 38 minutes, with zero restart requests
// across both. All three are boot-time env on the same MCP child
// (SLACK_PEER_AGENT_MODE / SLACK_PEER_GROUP_IDS / SLACK_THIRD_PARTY_BOTS, read
// once at module load in packages/mcp/src/slack-channel.ts).
//
// TWO KEYS ARE DELIBERATELY STILL OUT, and both were candidates:
//
//   `peers` — NOT a channel-config key at all. SLACK_PEERS / SLACK_PEERS_GATE
//   are built from the server-resolved roster the adapter receives as
//   `options.slackPeers`, never from `config['peers']`. Adding it would hash
//   `undefined` forever and could never move: an inert field that reads like
//   coverage. (The originating ticket lists it as a fourth key; it is not one.)
//
//   `ping_allowed_users` — emitted as SLACK_PING_ALLOWED_USERS, but ENG-6259
//   made the /ping and diagnostic allowlists re-read LIVE from .mcp.json
//   (packages/mcp/src/slack-allowlist-source.ts), specifically so they do not
//   depend on the manager's version. It self-heals from disk, so it is not a
//   last-hop-gap key. `allowed_users` stays in the subset regardless because
//   ENG-7779's fail-closed unknown-baseline restart is keyed on it.
//
// The normalisation for all three comes from @augmented/core, the SAME
// functions the adapter uses to write the env. A hand-mirrored filter is what
// this file has done three times before, and the mirror is exactly what rots.
//
// ENG-6464: `ack_reaction` and `skip_reaction` are now INCLUDED. They were
// excluded while ack_reaction had no fleet consumer (the slack-channel MCP
// hardcoded 'eyes'); ENG-6464 made them real env consumers
// (SLACK_ACK_REACTION / SLACK_SKIP_REACTION, read once at MCP boot to render
// the ack emoji + the seen-but-skipping instruction). So an operator changing
// either emoji must restart the session to take effect — the same gap the
// trigger closes for every other behaviour field. The decision's first-poll
// baseline means adding these fields does NOT restart on deploy; only a later
// operator change does.

/**
 * The Slack behaviour fields that map to slack-channel MCP boot-time env
 * vars (claudecode adapter, persistent slack entry). Values are normalised
 * to the adapter's own absent-field defaults so an explicit save of the
 * default value hashes identically to the field being absent — the env
 * output is the same, so no restart is warranted.
 */
import {
  buildSlackThirdPartyBotsEnvValue,
  normaliseSlackPeerAgentMode,
  normaliseSlackPeerGroupIds,
} from '@augmented/core';

export interface SlackBehaviourSubset {
  thread_auto_follow: string;
  channel_response_mode: string;
  /** ENG-6464: ack/skip reaction emoji shortcodes. The adapter emits
   *  SLACK_ACK_REACTION / SLACK_SKIP_REACTION only when non-empty; normalise
   *  absent → '' so a config without them hashes identically to one with them
   *  explicitly cleared (both ⇒ no env var). */
  ack_reaction: string;
  skip_reaction: string;
  /** Normalised: trimmed non-empty strings only, original order preserved
   *  (the adapter join is order-sensitive; reordering IS an env change but
   *  the webapp always writes a de-duped parse of the input, so an
   *  unchanged save round-trips identically). Empty/absent → []. */
  allowed_users: string[];
  /** ENG-10028: '' when the adapter emits no SLACK_PEER_AGENT_MODE. `off`,
   *  absent and malformed all collapse to '' because all three produce the same
   *  (missing) env var — normalising them apart would restart the session on a
   *  change that writes an identical .mcp.json. */
  peer_agent_mode: string;
  peer_group_ids: string[];
  /** ENG-10028: the ids that reach SLACK_PEER_GROUP_IDS, ORDER PRESERVED — the
   *  adapter emits a join(',') and the classifier splits it, so a reorder is a
   *  genuinely different env value. */
  /** ENG-10028: the exact SLACK_THIRD_PARTY_BOTS string, or '' when the adapter
   *  emits nothing. Held as the SERIALISED value rather than the parsed array
   *  on purpose: the adapter validates and drops malformed entries, so two
   *  configs that differ only in rejected garbage produce identical env and
   *  must hash identically. Comparing the parsed input would restart on a
   *  change the running process can never observe. */
  third_party_bots: string;
  // ENG-6504: block_kit_enabled / block_kit_ask_user_enabled were removed from
  // the subset — the adapter now hardwires SLACK_BLOCK_KIT_ENABLED /
  // SLACK_BLOCK_KIT_ASK_USER_ENABLED to 'true' for every Slack channel, so
  // their env output is constant and can never move the hash.
}

export function extractSlackBehaviourSubset(
  config: Record<string, unknown> | null | undefined,
): SlackBehaviourSubset {
  const rawAllowed = config?.['allowed_users'];
  return {
    thread_auto_follow:
      (config?.['thread_auto_follow'] as string | undefined) ?? 'off',
    channel_response_mode:
      (config?.['channel_response_mode'] as string | undefined) ?? 'mention_only',
    // ENG-6464: trimmed string, absent → '' (matches the adapter's
    // emit-only-when-truthy behaviour, so unset and "" hash identically).
    ack_reaction: ((config?.['ack_reaction'] as string | undefined) ?? '').trim(),
    skip_reaction: ((config?.['skip_reaction'] as string | undefined) ?? '').trim(),
    // Mirror the adapter's defensive filter exactly — a malformed entry the
    // adapter would drop must not count as an env change.
    allowed_users: Array.isArray(rawAllowed)
      ? rawAllowed
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
          .map((v) => v.trim())
      : [],
    // ENG-10028: the adapter's own functions, not a copy of them.
    peer_agent_mode: normaliseSlackPeerAgentMode(config?.['peer_agent_mode']),
    peer_group_ids: normaliseSlackPeerGroupIds(config?.['peer_group_ids']),
    third_party_bots: buildSlackThirdPartyBotsEnvValue(config?.['third_party_bots']) ?? '',
  };
}

/**
 * ENG-7779: whether the behaviour subset actually restricts anything.
 * `allowed_users` is an access-control gate (who may run the diagnostic /
 * restart slash commands), so an UNKNOWN delivery baseline with a non-empty
 * allowlist must fail closed - restart the live session to guarantee the
 * running slack-channel MCP enforces it - exactly like a restrictive
 * sender_policy under ENG-7771. The other fields (thread_auto_follow,
 * channel_response_mode, reactions) are cosmetic/behavioural: an unknown
 * baseline exposes nothing, so first poll re-baselines without a restart.
 */
export function isSlackBehaviourRestrictive(subset: SlackBehaviourSubset): boolean {
  return subset.allowed_users.length > 0;
}

// The restart decision itself (first-poll baseline, no-change debounce,
// persistent/claude-code/session-healthy gates, same-tick restart
// suppression) is identical to the sender-policy trigger — reuse it under a
// call-site-accurate name rather than duplicating the tree.
export { decideSenderPolicyRestart as decideSlackBehaviourRestart } from './sender-policy-restart-decision.js';
